import authAxios from '../../../core/api/interceptors/authAxios';

/**
 * Access Management Service
 * Handles all API calls related to role and user permissions management
 */

const API_BASE = '/api';

/**
 * Get all available modules/features with their permissions
 * @returns {Promise} Array of modules with permissions
 */
export const getAllModules = async () => {
  try {
    const response = await authAxios.get(`/AccessManagement/modules`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all roles in the company
 * @returns {Promise} Array of roles
 */
export const getCompanyRoles = async () => {
  try {
    const response = await authAxios.get(`/AccessManagement/roles`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all users in the company
 * @returns {Promise} Array of users
 */
export const getCompanyUsers = async () => {
  try {
    const response = await authAxios.get(`/AccessManagement/users`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get permissions for a specific role
 * @param {string} roleId - Role ID
 * @returns {Promise} Role permissions object
 */
export const getRolePermissions = async (roleId) => {
  try {
    const response = await authAxios.get(`/AccessManagement/roles/${roleId}/permissions`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get permissions for a specific user
 * @param {string} userId - User ID
 * @returns {Promise} User permissions object
 */
export const getUserPermissions = async (userId) => {
  try {
    const response = await authAxios.get(`/AccessManagement/users/${userId}/permissions`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update permissions for a role
 * @param {string} roleId - Role ID
 * @param {Object} permissions - Permissions object { ModuleName: { CanView, CanEdit, CanCreate, CanDelete } }
 * @returns {Promise} Updated role permissions
 */
export const updateRolePermissions = async (roleId, permissions) => {
  try {
    const response = await authAxios.put(`/AccessManagement/roles/${roleId}/permissions`, {
      Permissions: permissions
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update permissions for a specific user (overrides role permissions)
 * @param {string} userId - User ID
 * @param {Object} permissions - Permissions object { ModuleName: { CanView, CanEdit, CanCreate, CanDelete } }
 * @returns {Promise} Updated user permissions
 */
export const updateUserPermissions = async (userId, permissions) => {
  try {
    // If caller passes { CompanyId, Permissions }, send as-is; otherwise wrap for backward compat
    const body = permissions?.Permissions
      ? permissions
      : { Permissions: permissions };
    const response = await authAxios.put(`/AccessManagement/users/${userId}/permissions`, body);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Bulk update permissions for multiple modules for a role
 * @param {string} roleId - Role ID
 * @param {Array} updates - Array of { ModuleId, ModuleName, CanView, CanEdit, CanCreate, CanDelete }
 * @returns {Promise} Updated permissions
 */
export const bulkUpdateRolePermissions = async (roleId, updates) => {
  try {
    const response = await authAxios.post(`/AccessManagement/roles/${roleId}/permissions/bulk`, {
      Updates: updates
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Bulk update permissions for multiple modules for a user
 * @param {string} userId - User ID
 * @param {Array} updates - Array of { ModuleId, ModuleName, CanView, CanEdit, CanCreate, CanDelete }
 * @returns {Promise} Updated permissions
 */
export const bulkUpdateUserPermissions = async (userId, updates) => {
  try {
    const response = await authAxios.post(`/AccessManagement/users/${userId}/permissions/bulk`, {
      Updates: updates
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Reset user permissions to role defaults
 * @param {string} userId - User ID
 * @returns {Promise} Success message
 */
export const resetUserToRoleDefaults = async (userId) => {
  try {
    const response = await authAxios.post(`/AccessManagement/users/${userId}/permissions/reset`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get permission change history
 * @param {Object} filters - Optional filters { userId, roleId, startDate, endDate }
 * @returns {Promise} Array of permission change logs
 */
export const getPermissionHistory = async (filters = {}) => {
  try {
    const params = new URLSearchParams(filters).toString();
    const response = await authAxios.get(`/AccessManagement/history?${params}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a new module
 * @param {Object} moduleData - Module data { name, description, category }
 * @returns {Promise} Created module
 */
export const createModule = async (moduleData) => {
  try {
    const response = await authAxios.post(`/AccessManagement/modules`, moduleData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update an existing module
 * @param {string} moduleId - Module ID
 * @param {Object} moduleData - Module data { name, description, category }
 * @returns {Promise} Updated module
 */
export const updateModule = async (moduleId, moduleData) => {
  try {
    const response = await authAxios.put(`/AccessManagement/modules/${moduleId}`, moduleData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete a module
 * @param {string} moduleId - Module ID
 * @returns {Promise} Success message
 */
export const deleteModule = async (moduleId) => {
  try {
    const response = await authAxios.delete(`/AccessManagement/modules/${moduleId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default {
  getAllModules,
  getCompanyRoles,
  getCompanyUsers,
  getRolePermissions,
  getUserPermissions,
  updateRolePermissions,
  updateUserPermissions,
  bulkUpdateRolePermissions,
  bulkUpdateUserPermissions,
  resetUserToRoleDefaults,
  getPermissionHistory,
  createModule,
  updateModule,
  deleteModule,
};
